import React, { useCallback, useState } from 'react';
import { CircularProgress, Grid, Link, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import InformativeCard from '../InformativeCard';
import loginFailed from '../../../../assets/images/enter.png';
import CustomDialog from '../../../../components/CustomDialog';
import CustomDataTable from '../../../../components/CustomDataTable';
import { LogDistanceDTO } from '../../../../models/dto/LogDistanceDTO';
import { findAllFailedLoginAttempts } from '../../../../services/personnelDepartment.service';
import { useFeedback } from '../../../../hooks/feedback';
import { AppState } from '../../../../store';
import ImagePreviewDialog, {
  ImageData,
} from '../../../../components/ImagePreviewDialog';
import { getLogDistanceImageData } from '../../../../services/peopleManagement.service';
import { CondominiumReducerData } from '../../../../store/reducers/condominiumReducer';

const FailedLoginAttemptCard: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [openImagePreviewDialog, setOpenImagePreviewDialog] = useState<boolean>(
    false,
  );
  const [loginAttempts, setLoginAttempts] = useState<LogDistanceDTO[]>([]);
  const [logDistanceImageSelected, setLogDistanceImageSelected] = useState<
    ImageData | undefined
  >();
  const { condominiumSelected }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );
  const { addFeedback } = useFeedback();

  const handleCardClick = useCallback(async () => {
    try {
      setLoginAttempts([]);
      setOpenDialog(true);
      setLoading(true);
      if (condominiumSelected?.referencia) {
        const reference = condominiumSelected.referencia;
        const { data } = await findAllFailedLoginAttempts(reference);
        setLoginAttempts(data);
      }
    } catch (error) {
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao buscar as informações.',
      });
    } finally {
      setLoading(false);
    }
  }, [addFeedback, condominiumSelected]);

  const getLogDistanceImage = useCallback(
    async (fileHash: string) => {
      setLoadingImage(true);
      try {
        const data = await getLogDistanceImageData(fileHash);

        setLogDistanceImageSelected({
          contentType: data.contentType,
          fileBuffer: data.fileBuffer,
        });
      } catch (error) {
        addFeedback({
          type: 'error',
          description: 'Ocorreu um erro ao buscar a imagem.',
        });
      } finally {
        setLoadingImage(false);
      }
    },
    [addFeedback],
  );

  const handleOpenImagePreviewDialog = (fileHash: string) => {
    setOpenImagePreviewDialog(true);
    getLogDistanceImage(fileHash);
  };

  const columns = [
    {
      name: 'criadoEm',
      label: 'Data',
      options: {
        customBodyRender: (value: string, _tableMeta: any, _updateValue: any) =>
          value.split('', 10),
      },
    },
    {
      name: 'criadoEm',
      label: 'Horário',
      options: {
        customBodyRender: (value: string, _tableMeta: any, _updateValue: any) =>
          value.slice(11),
      },
    },
    {
      name: 'loginHash',
      label: 'Link para tentativa de login',
      options: {
        customBodyRender: (
          value: string,
          _tableMeta: any,
          _updateValue: any,
        ) => (
          <Link
            component="button"
            onClick={() => handleOpenImagePreviewDialog(value)}
            color="primary"
          >
            Visualizar
          </Link>
        ),
      },
    },
  ];

  return (
    <>
      <InformativeCard
        imgAltText="Tentativas de Login"
        imgSrc={loginFailed}
        cardTitle="Tentativas de Login"
        cardText="Verifique a lista dos funcionários que falharam na tentativa de logar no sistema."
        handleCardClick={handleCardClick}
      />
      <CustomDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => setOpenDialog(false)}
        showOneButton
        maxWidth="md"
        okButtonLabel="Fechar"
        showCloseButton
      >
        <CustomDataTable
          title={
            <Grid container direction="row">
              <Typography variant="h6">
                Funcionários que falharam na Tentativa de Login
              </Typography>
              {loading && (
                <CircularProgress
                  size={20}
                  style={{ marginLeft: 15, position: 'relative', top: 4 }}
                />
              )}
            </Grid>
          }
          data={loginAttempts}
          columns={columns}
          options={{ elevation: 0, download: true }}
        />
      </CustomDialog>
      <ImagePreviewDialog
        open={openImagePreviewDialog}
        title="A seguinte imagem foi utilizada"
        loading={loadingImage}
        image={logDistanceImageSelected}
        handleClose={() => setOpenImagePreviewDialog(false)}
      />
    </>
  );
};

export default FailedLoginAttemptCard;
